import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/*
 * v-reveal — fade/slide an element in once when it scrolls into view.
 *
 *   <div v-reveal>                       single element
 *   <div v-reveal="{ delay: 0.1, y: 40 }"> with options
 *   <ul v-reveal.stagger>                children animate one after another
 *
 * For lists rendered from API data, put v-reveal on each v-for item
 * (e.g. v-reveal="{ delay: (i % 4) * 0.08 }") because the children
 * don't exist yet when the parent mounts.
 */

const prefersReducedMotion = () =>
  window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const reveal = {
  mounted(el, binding) {
    if (prefersReducedMotion()) return;

    const opts = binding.value || {};
    const stagger = binding.modifiers.stagger;
    const targets = stagger ? Array.from(el.children) : el;
    if (stagger && targets.length === 0) return;

    el._revealTween = gsap.from(targets, {
      y: opts.y ?? 32,
      opacity: 0,
      duration: opts.duration ?? 0.8,
      delay: opts.delay ?? 0,
      stagger: stagger ? opts.stagger ?? 0.1 : 0,
      ease: "power3.out",
      // Drop inline styles afterwards so hover transforms keep working
      clearProps: "transform,opacity",
      scrollTrigger: {
        trigger: el,
        start: opts.start ?? "top 88%",
        once: true,
      },
    });
  },

  unmounted(el) {
    if (el._revealTween) {
      el._revealTween.scrollTrigger?.kill();
      el._revealTween.kill();
      el._revealTween = null;
    }
  },
};

export default {
  install(app) {
    app.directive("reveal", reveal);

    // Page height changes as API data and images load; keep trigger positions accurate.
    if (typeof ResizeObserver !== "undefined") {
      let timer;
      new ResizeObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(() => ScrollTrigger.refresh(), 200);
      }).observe(document.body);
    }
  },
};
